import * as React from 'react'
import { PropsWithChildren, ReactElement, useEffect } from 'react'
import { CreateDto } from '../../../config/filePartyConfig'
import { FormikConfig } from 'formik/dist/types'
import { AxiosInstance, AxiosResponse } from 'axios'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import { PartyAction } from '../../../config/partyConfig'
import Form from '../Form'
import EnumCombobox, { EnumComboboxProps } from '../../Combobox/EnumCombobox'
import { AttributeEntityDto, AttributesReturnProps } from '../../../hooks/useAttributes'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import { DimensionPivotValues } from '../Accounting/DimensionFields'
import { PartyTierDto } from '../../../config/finance/financeConfig'
import { RateDto } from '../../../config/fileConfig'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { DRIVE_ITEM_CONFIG } from '../../TextField/Folder/FolderField'

export type BasePartyMutationDto = AttributeEntityDto &
    RateDto<PartyTierDto> & {
        id?: string
        shortName?: string
        addressStreet?: string
        addressNumber?: string
        addressPostalCode?: string
        addressLocality?: string
        addressMunicipality?: string
        addressCountry?: string
        email: string
        phoneLandline: string
        status: PartyStatus
        type: PartyType
        pivot?: CreateDto
        accounting?: DimensionPivotValues
        crmRelation?: boolean
        accountManagerId?: string
        documentRootId?: string
        documentRootPath?: string // used in frontend only
        description?: string
    }

export type AddPartyProps = Pick<CreateDto, 'role' | 'parentId'>

export enum PartyStatus {
    Active = 'ACTIVE',
    Inactive = 'INACTIVE',
}

export enum PartyType {
    Legal = 'LEGAL',
    Natural = 'NATURAL',
}

export async function applyDocumentRootPath(axios: AxiosInstance, translate: (key: string) => string, party: BasePartyMutationDto): Promise<void> {
    if (party?.documentRootPath) {
        let response: AxiosResponse<DriveItem>
        let lastPathId = undefined
        let currentPath = ''

        const fragments: string[] = party.documentRootPath.split('/')
        while (fragments.length) {
            const fragment = fragments.shift()
            currentPath = currentPath + '/' + fragment

            response = await axios
                .get<DriveItem>('/root:' + currentPath, DRIVE_ITEM_CONFIG)
                .then(response => response)
                .catch(() => undefined)
            if (fragments.length && response?.data?.id) {
                //where it's not the file's folder and the folder exists.
                lastPathId = response.data.id
            } else if (fragments.length) {
                //where it's not the file's folder.
                response = await axios.post<DriveItem>(
                    !lastPathId ? '/root/children' : `/items/${lastPathId}/children`,
                    { name: fragment, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' },
                    DRIVE_ITEM_CONFIG,
                )
                lastPathId = response.data.id
            } else if (party.documentRootId) {
                //if the file's folder was created before we need to move&rename it.
                response = await axios.patch<DriveItem>(
                    `/items/${party.documentRootId}`,
                    { name: fragment, ...(lastPathId ? { parentReference: { id: lastPathId } } : {}) },
                    DRIVE_ITEM_CONFIG,
                )
                lastPathId = response.data.id
            } else {
                //if the file's folder was not created before.
                response = await axios.post<DriveItem>(
                    !lastPathId ? '/root/children' : `/items/${lastPathId}/children`,
                    { name: fragment, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' },
                    DRIVE_ITEM_CONFIG,
                )
                lastPathId = response.data.id
            }
        }

        if (!lastPathId) {
            return Promise.reject(translate('file.edit.form.error.sharepoint'))
        }

        party.documentRootId = lastPathId
        return Promise.resolve()
    }
}

export function PartyStatusDropdown(): ReactElement {
    return (
        <FormikComboboxField<BasePartyMutationDto, EnumComboboxProps<PartyStatus>>
            labelKey={'party.status.label'}
            fieldName={'status'}
            component={EnumCombobox}
            componentProps={{
                entities: [PartyStatus.Active, PartyStatus.Inactive],
                labelKeyPrefix: 'party.status',
                clearable: false,
            }}
        />
    )
}

export type PartyProps<D extends BasePartyMutationDto> = {
    formikProps: Omit<FormikConfig<D>, 'onSubmit'>
    onSubmit: (party: D) => Promise<AxiosResponse<string>>
    goBack: () => void
    attributes: AttributesReturnProps
}

export default function PartyForm<D extends BasePartyMutationDto>(props: PropsWithChildren<PartyProps<D>>): ReactElement {
    const dispatch = useDispatch()

    useEffect(() => {
        return () => {
            dispatch({ type: PartyAction.Reset })
        }
    }, [])

    return (
        <Formik<D>
            {...props.formikProps}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (party: D) => {
                await props.onSubmit({
                    ...party,
                    pivot: party.pivot ?? undefined,
                })
                props.goBack()
            }}
        >
            <Form>{props.children}</Form>
        </Formik>
    )
}
